import { createInstance, type i18n as I18nInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { DEFAULT_LOCALE, DEFAULT_NAMESPACE, LOCALES, NAMESPACES, type Locale } from './config';
import { resources } from './resources';

export interface CreateI18nOptions {
  locale?: Locale | undefined;
}

/**
 * Build a fresh i18next instance.
 *
 * A factory, not a module-level singleton that initialises on import. Three
 * reasons, in increasing order of how much they hurt when ignored:
 *
 *   - Tests get an isolated instance each, so one changing language cannot
 *     leak into the next.
 *   - The landing site's static prerender (P0-LAND-01) renders each locale in
 *     the same process; a shared mutable instance would race.
 *   - Import-time side effects run before an app has decided anything, which
 *     is how a bundle ends up locked to a locale chosen at module-eval time.
 *
 * Resolves synchronously: every catalogue is already in the bundle, so there is
 * no backend to await and no loading state for a consumer to render.
 */
export function createI18n({ locale = DEFAULT_LOCALE }: CreateI18nOptions = {}): I18nInstance {
  const instance = createInstance();

  void instance.use(initReactI18next).init({
    lng: locale,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...LOCALES],
    ns: [...NAMESPACES],
    defaultNS: DEFAULT_NAMESPACE,
    resources,
    interpolation: {
      // React escapes for us. Doing it twice renders &amp;amp; in the UI.
      escapeValue: false,
    },
    returnNull: false,
  });

  return instance;
}
